import React, { useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import { Input } from '@/components/forms/Input/Input';
import { PrimaryButton } from '@/components/buttons/PrimaryButton/PrimaryButton';
import IconSpinner from '@/assets/icons/icon-spiner.svg?react';
import IconCheckmarkCircleFill from '@/assets/icons/icon-checkmark-circle-fill.svg?react';

export type NavbarSavePhase = 'idle' | 'saving' | 'saved';

interface NavbarSaveProps {
  className?: string;
  header: string;
  value: string;
  placeholder?: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  // Optional: allow controlled phase; if omitted, component manages its own phase
  phase?: NavbarSavePhase;
  // Optional: external callback to run on save click; internal phase handling still applies when uncontrolled
  onSave?: () => void;
  // Optional: called when the component reaches the 'saved' phase
  onSaved?: () => void;
}

export const NavbarSave: React.FC<NavbarSaveProps> = ({
  className = '',
  header,
  value = '',
  placeholder,
  onChange,
  phase,
  onSave,
  onSaved,
}) => {
  // Internal phase state for uncontrolled usage
  const [internalPhase, setInternalPhase] = useState<NavbarSavePhase>('idle');

  // Use controlled phase when provided; otherwise fall back to internal state
  const effectivePhase = phase ?? internalPhase;

  // Check if value is valid for saving
  const isValidValue = value.trim().length > 0;

  const handleSave = useCallback(() => {
    // Prevent re-entrant saves
    if (effectivePhase !== 'idle') return;

    // Validate value before saving
    const trimmedValue = value.trim();
    if (!trimmedValue) {
      console.warn('Cannot save with empty value');
      return;
    }

    // Invoke external callback if provided (e.g., to persist data)
    if (onSave) onSave();

    // When uncontrolled, manage the phase transitions internally
    if (phase === undefined) {
      setInternalPhase('saving');
      setTimeout(() => {
        setInternalPhase('saved');
        // Notify when reaching 'saved'
        if (onSaved) onSaved();
        setTimeout(() => setInternalPhase('idle'), 1500);
      }, 1000);
    }
  }, [effectivePhase, onSave, onSaved, phase, value]);

  // In controlled mode, notify when external phase reaches 'saved'
  useEffect(() => {
    if (phase === 'saved' && onSaved) onSaved();
  }, [phase, onSaved]);

  return (
    <div
      className={clsx(
        'bg-neutral-0 inline-flex w-[280px] flex-col items-stretch gap-3 rounded-lg border border-neutral-100 pb-2 shadow-[0px_12px_24px_0px_rgba(0,0,0,0.12)]',
        className,
      )}
    >
      <div className="bg-neutral-0 inline-flex h-10 items-center justify-between rounded-t-lg border-b border-neutral-100 pr-2 pl-3">
        <div className="flex flex-1 items-center py-1">
          <span className="text-xs leading-none font-medium text-neutral-900">{header}</span>
        </div>
      </div>

      <div className="flex flex-col gap-3 px-3 pt-2 pb-1">
        <div className="flex flex-col gap-2">
          <div className="w-64">
            <Input
              label=""
              value={value}
              onChange={onChange}
              placeholder={placeholder}
              className="w-full"
              containerClassName="w-full"
            />
          </div>
        </div>
        {effectivePhase === 'idle' && (
          <PrimaryButton
            icon={null}
            size="sm"
            state={isValidValue ? 'default' : 'disabled'}
            text="Save"
            className="h-7 w-full"
            onClick={handleSave}
            data-qa-selector="navbar-save-button"
          />
        )}
        {effectivePhase === 'saving' && (
          <PrimaryButton
            icon={IconSpinner}
            size="sm"
            state="loading"
            text="Saving..."
            className="h-7 w-full"
            disabled
            data-qa-selector="navbar-save-button"
          />
        )}
        {effectivePhase === 'saved' && (
          <PrimaryButton
            icon={IconCheckmarkCircleFill}
            size="sm"
            state="default"
            text="Saved"
            className="h-7 w-full"
            disabled
            data-qa-selector="navbar-save-button"
          />
        )}
      </div>
    </div>
  );
};
